import * as Dialog from '@radix-ui/react-dialog'
import { useMemo, useState } from 'react'
import { useToastService } from '../../services/toast-service.tsx'
import {
  UNGROUPED_PROJECT_FILTER,
  useCreateProjectWithRepositories,
  useGetRepositories,
} from '../../utils/api-client/api-hooks.ts'
import type { Project } from '../../utils/api-client/api-schemas.ts'
import type { AvailableTemplate } from '../../utils/api-client/templates.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { SearchInput } from '../input/search-input.tsx'
import { useShowProjectMutationError } from '../projects/admin-required-override.ts'
import { toPickableRepository } from '../projects/pickable-repositories.ts'
import { isDuplicateProjectNameError } from '../projects/project-constants.ts'
import { ProjectDetailsFields } from '../projects/project-details-fields.tsx'
import * as styles from '../projects/project-dialog.css.ts'
import { RepositoryPickerSection } from '../projects/repository-picker-section.tsx'
import { SelectionSummary } from '../projects/selection-summary.tsx'
import {
  SEARCH_PAGE_SIZE,
  SELECTION_CAP,
  selectionCapReason,
  useRepositoryPicker,
} from '../projects/use-repository-picker.ts'

export type CreateProjectModalProps = {
  template: AvailableTemplate
  onClose: () => unknown
  onCreated: (project: Project) => unknown
  /**
   * Returns focus to whatever opened this dialog. Radix restores to a `Dialog.Trigger`, and this
   * dialog has none — it mounts already open. @see useReturnFocus
   */
  onCloseAutoFocus?: (event: Event) => void
}

/**
 * Names a project, optionally describes it, and picks the ungrouped repositories to start it with.
 * The prompt pre-selects candidates; nothing is assigned until the project exists.
 */
export const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  template,
  onClose,
  onCreated,
  onCloseAutoFocus,
}) => {
  const { addToast } = useToastService()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [nameError, setNameError] = useState<string>()

  // The search is the API's: what the reader types goes to `GET /repositories?name=`, AND-ed with
  // `project=ungrouped`, rather than narrowing a window fetched up front. An organization with
  // thousands of repositories searches the same way as one with ten.
  const [search, setSearch] = useState('')
  const {
    data: ungrouped,
    isPending: repositoriesPending,
    isError: repositoriesFailed,
  } = useGetRepositories({
    pageIndex: 0,
    pageSize: SEARCH_PAGE_SIZE,
    searchTerm: search,
    projectIds: [UNGROUPED_PROJECT_FILTER],
  })
  const candidates = useMemo(() => (ungrouped?._embedded.items ?? []).map(toPickableRepository), [ungrouped])
  const picker = useRepositoryPicker()

  const showAssignError = useShowProjectMutationError('Project created, but adding repositories failed')
  const showCreateError =
    useShowProjectMutationError<typeof useCreateProjectWithRepositories>('Failed to create project')
  const createMutation = useCreateProjectWithRepositories({
    // The project exists either way, so the reader lands on it either way — with the error instead of
    // the success toast when its repositories did not follow, which is the state the page will show.
    onSuccess: ({ project, assignmentError, isAssignmentWithheld }, variables, onMutateResult, context) => {
      if (isAssignmentWithheld) {
        addToast({ message: <>Project created, but you can&apos;t add repositories to it.</>, variant: 'error' })
      } else if (assignmentError) {
        showAssignError(assignmentError, variables, onMutateResult, context)
      } else {
        addToast({ message: <>Project &ldquo;{project.name}&rdquo; created</>, variant: 'success' })
      }
      onCreated(project)
    },
    onError: (error, ...rest) => {
      if (isDuplicateProjectNameError(error)) {
        setNameError(`A project named ${name.trim()} already exists.`)
        return
      }
      showCreateError(error, ...rest)
    },
  })

  const isPending = createMutation.isPending
  const trimmedName = name.trim()
  const trimmedDescription = description.trim()

  const handleCreate = () => {
    if (trimmedName === '') {
      return
    }
    setNameError(undefined)
    createMutation.mutate({
      template,
      body: {
        name: trimmedName,
        description: trimmedDescription === '' ? undefined : trimmedDescription,
      },
      repositoryIds: [...picker.pickedIds],
    })
  }

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !isPending) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.content}
          onPointerDownOutside={event => event.preventDefault()}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <Dialog.Title className={styles.title}>Create project</Dialog.Title>
          <Dialog.Description hidden>Name the project and pick the repositories it starts with.</Dialog.Description>

          <ProjectDetailsFields
            name={name}
            onNameChange={value => {
              setName(value)
              setNameError(undefined)
            }}
            nameError={nameError}
            description={description}
            onDescriptionChange={setDescription}
          />

          <SearchInput ariaLabel="Search repositories" value={search} onChange={setSearch} />

          <RepositoryPickerSection
            label="Ungrouped repositories"
            filteredLabel="Matching repositories"
            emptyMessage="Every repository already belongs to a project."
            candidates={candidates}
            total={ungrouped?.total}
            pickedIds={picker.pickedIds}
            onToggle={picker.toggle}
            unpickedDisabledReason={picker.isAtSelectionCap ? selectionCapReason(SELECTION_CAP) : undefined}
            filter={search}
            isFiltering={search.trim() !== ''}
            isPending={repositoriesPending}
            isError={repositoriesFailed}
          />

          <div className={styles.bar}>
            <SelectionSummary selectedCount={picker.pickedIds.size} onClear={picker.clearPicked} />
            <span className={styles.buttons}>
              <GhostButton
                type="secondary"
                className={styles.outlinedGhost}
                onClick={onClose}
                state={isPending ? 'disabled' : 'default'}
              >
                Cancel
              </GhostButton>
              <DefaultButton
                state={isPending ? 'loading' : trimmedName === '' ? 'disabled' : 'default'}
                onClick={handleCreate}
                loadingLabel="Creating project"
              >
                Create project
              </DefaultButton>
            </span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
